/**
 * Sanitize user-supplied theme HTML before persisting or rendering.
 *
 * Removes:
 *  - Dangerous tags (script, iframe, object, embed, link, meta, base, style,
 *    form, input, button, textarea, select, option, svg/use with external refs)
 *  - Event-handler attributes (on*)
 *  - URL attributes with javascript:/vbscript:/data:text-html schemes
 *  - Inline style attributes containing expression(), javascript:,
 *    url(javascript:...), @import, behaviour:, behavior:
 *
 * Runs both when the user clicks Apply (so state is clean) and when rendering
 * a live Preview (defence in depth).
 */

const BLOCKED_TAGS = new Set([
  "script",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "base",
  "style",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
]);

const URL_ATTRS = new Set([
  "href",
  "src",
  "xlink:href",
  "action",
  "formaction",
  "poster",
  "srcset",
  "ping",
]);
// Blocks javascript:/vbscript: plus all non-image data: URLs. data:image/... is
// still allowed because authors legitimately paste base64-encoded image URLs.
const DANGEROUS_URL_RE = /^\s*(javascript|vbscript|data:(?!image\/))/i;
const DANGEROUS_CSS_RE =
  /expression\s*\(|javascript\s*:|vbscript\s*:|behaviour\s*:|behavior\s*:|@import|url\s*\(\s*['"]?\s*(javascript|vbscript)/i;

function walk(node) {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType !== 1) continue;
    const tag = child.tagName.toLowerCase();
    if (BLOCKED_TAGS.has(tag)) {
      child.remove();
      continue;
    }
    const attrs = Array.from(child.attributes);
    for (const attr of attrs) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        child.removeAttribute(attr.name);
        continue;
      }
      if (URL_ATTRS.has(name) && DANGEROUS_URL_RE.test(attr.value)) {
        child.removeAttribute(attr.name);
        continue;
      }
      if (name === "style" && DANGEROUS_CSS_RE.test(attr.value)) {
        child.removeAttribute(attr.name);
        continue;
      }
    }
    walk(child);
  }
}

export function sanitizeThemeHtml(raw) {
  if (typeof raw !== "string" || raw.trim() === "") return "";
  if (typeof DOMParser === "undefined") {
    // Non-browser environment without DOMParser (rare). Fall back to a
    // conservative string-level strip of script tags and on* handlers.
    return raw
      .replace(
        /<\s*(script|iframe|object|embed|style|link|meta|base)[\s\S]*?>[\s\S]*?<\s*\/\s*\1\s*>/gi,
        ""
      )
      .replace(/<\s*(script|iframe|object|embed|style|link|meta|base)[^>]*\/?\s*>/gi, "")
      .replace(/\son[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "")
      .replace(
        /\s(href|src|action|formaction|poster)\s*=\s*["']?\s*(javascript|vbscript):[^"'\s>]*["']?/gi,
        ""
      );
  }
  const doc = new DOMParser().parseFromString(
    `<div id="__sanitize_root__">${raw}</div>`,
    "text/html"
  );
  const root = doc.getElementById("__sanitize_root__");
  if (!root) return "";
  walk(root);
  return root.innerHTML;
}

export default sanitizeThemeHtml;
